import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import Qs from 'qs'
import notification from './notifications'
import { findDeepProp } from './helpers'
import { useAuthStore } from '@/stores/auth'

const serverNotRespondingMessage = (status: number | string) =>
  `Our server is not responding (${status}), please try again later,
if the problem persists call the system administrator.`

const headers = {
  'Cache-control': 'no-cache',
  Pragma: 'no-cache',
  Expires: '0',
}

// Versioned API: every call is written relative to /api/v1.
axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
axios.defaults.paramsSerializer = {
  serialize: (params) => Qs.stringify(params, { arrayFormat: 'indices' }),
}
Object.assign(axios.defaults.headers.common, headers)

/**
 * Keys whose values axios replaces with a placeholder when it serialises a
 * failed request's config, so a logged error never carries the bearer token.
 */
const REDACTED_KEYS = ['authorization', 'cookie', 'set-cookie']

/** Attaches the XGuard API bearer token. */
const apiRequest = async (config: InternalAxiosRequestConfig) => {
  const token = await useAuthStore().getApiToken()
  config.headers.Authorization = `Bearer ${token}`
  config.redact = REDACTED_KEYS
  return config
}

/** Attaches the Microsoft Graph bearer token. */
const graphRequest = async (config: InternalAxiosRequestConfig) => {
  const token = await useAuthStore().getToken()
  config.headers.Authorization = `Bearer ${token}`
  config.redact = REDACTED_KEYS
  return config
}

/**
 * A 401 or 403 means the token was refused. The old client swallowed these and
 * left the promise pending forever; now the caller sees the rejection and
 * defaultResponseController tells the user.
 */
const responseError = (error: AxiosError & { unnotified?: boolean }) => {
  error.unnotified = true
  return Promise.reject(error)
}

axios.interceptors.request.use(apiRequest)
axios.interceptors.response.use(null, responseError)

/** Error controller: turns an axios failure into a user-facing notification. */
export const defaultResponseController = (error: unknown): void => {
  const dataErrors = findDeepProp('response.data.errors', error) as Record<string, string[]> | null
  if (dataErrors !== null && Object.keys(dataErrors).length > 0) {
    let errors = ''
    for (const field in dataErrors) {
      errors += `${dataErrors[field].join('\n')},`
    }
    notification.warning(errors.slice(0, -1), { duration: 10000 })
    return
  }

  const dataStatus = findDeepProp('response.status', error) as number | null

  if (dataStatus === 401 || dataStatus === 403) {
    notification.warning('Your session is not authorised for that. Sign out and back in, and if it keeps happening contact your supervisor.')
    return
  }

  // Problem Details from the API carry a title.
  const dataTitle = findDeepProp('response.data.title', error)
  if (typeof dataTitle === 'string' && dataStatus !== 500) {
    notification.warning(dataTitle)
    return
  }

  const dataMessage = findDeepProp('response.data.message', error)
  if (typeof dataMessage === 'string') {
    notification.warning(dataMessage)
    return
  }

  // Ignore (for now) status code 404 except on writes
  if (dataStatus === 404) {
    const method = findDeepProp('response.config.method', error)
    if (typeof method === 'string' && ['post', 'put', 'delete'].includes(method)) {
      notification.error(serverNotRespondingMessage(dataStatus))
    }
    return
  }

  if (typeof dataStatus === 'number' && dataStatus >= 500) {
    notification.error(serverNotRespondingMessage(dataStatus))
    return
  }

  console.error(error)
}

export const axiosGraph = axios.create({
  baseURL: import.meta.env.VITE_GRAPH_BASE_URL || '',
})
axiosGraph.interceptors.request.use(graphRequest)

export default axios
